import {useTheme} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import {forwardRef, useEffect, useState} from 'react';
import {HeaderBar} from './HeaderBar';
import {HeaderStateContext} from './HeaderStateContext';
import {HeaderProps, HeaderStateProps} from './types';

export const Header = forwardRef<HTMLDivElement, HeaderProps>(({
    children, ...props
}, ref) => {
    const theme = useTheme();

    const isSmallMobile = useMediaQuery(theme.breakpoints.down('xs'));
    const isMobile = useMediaQuery(theme.breakpoints.between('xs', 'sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const isLaptop = useMediaQuery(theme.breakpoints.between('md', 'lg'));
    const isDesktop = useMediaQuery(theme.breakpoints.between('lg', 'xl'));
    const isWideScreen = useMediaQuery(theme.breakpoints.up('xl'));

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(Boolean(props.isMobileMenuOpen));

    const handleMobileMenuClick: HeaderStateProps['handleMobileMenuClick'] = (event) => {
        if (props.onMobileMenuClick) {
            props.onMobileMenuClick(event, isMobileMenuOpen);
        }
    };

    const handleMobileMenuOpen: HeaderStateProps['handleMobileMenuOpen'] = () => {
        setIsMobileMenuOpen(true);
    };

    const handleMobileMenuClose: HeaderStateProps['handleMobileMenuClose'] = () => {
        setIsMobileMenuOpen(false);
    };

    useEffect(() => {
        if (props.onScreenSizeChange) {
            props.onScreenSizeChange({
                isSmallMobile,
                isMobile,
                isTablet,
                isLaptop,
                isDesktop,
                isWideScreen
            });
        }
    }, [isSmallMobile, isMobile, isTablet, isLaptop, isDesktop, isWideScreen]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <HeaderStateContext.Provider
            value={{
                // CurrentSizes
                isSmallMobile: props.isSmallMobile || isSmallMobile,
                isMobile: props.isMobile || isMobile,
                isTablet: props.isTablet || isTablet,
                isLaptop: props.isLaptop || isLaptop,
                isDesktop: props.isDesktop || isDesktop,
                isWideScreen: props.isWideScreen || isWideScreen,

                // HeaderEventHandlers
                handleMobileMenuClick,
                handleMobileMenuOpen,
                handleMobileMenuClose,

                // booleans
                ...props,

                isMobileMenuOpen
            }}
        >
            <HeaderBar ref={ref}>
                {children}
            </HeaderBar>
        </HeaderStateContext.Provider>
    );
});

Header.defaultProps = {
    skipLink: '#main-content',
    skipLinkText: 'Skip to the main content',

    title: 'Application Title',
    titleLink: '/',

    logoLink: '/',
    logoLinkText: 'Go to home',

    variant: 'default',

    hideMobileTitle: false,
    hideMobileMenu: false,
    showSearch: false,
    hideBranding: false,
    hideNavigation: false,
    isContained: false,
    isMobileMenuOpen: false,
    mobileMenuPosition: 'right',

    navigationItems: []
};

Header.displayName = 'Header';
